import { forwardRef, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Modal, Input, Space, Progress } from 'antd';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _invoke from 'lodash/invoke';
import { Formik, Form } from 'formik';
import { CameraOutlined, CloseCircleFilled, FileAddFilled } from '@ant-design/icons';
import { TextInput } from 'components/common';
import { useDebounce } from 'hooks';
import Helper from 'utils/helpers';
import {
  fetchProductPreviewRequest,
  clearProductPreview,
} from 'providers/MessengerProvider/slice';
import ProductPreviewTooltip from './ProductPreviewTooltip';
import './styles.less';
import SendIcon from 'assets/images/send.svg';
import PropTypes from 'prop-types';
import ReactPlayer from 'react-player';
import TemplateSelector from '../../TemplateSelector';

// eslint-disable-next-line react/display-name
const MessageInput = forwardRef(({ onClick, conversationId, uploading, uploadProgress = 0, isSending = false }, ref) => {
  const dispatch = useDispatch();
  const productPreview = useSelector((state) => state.messenger.productPreview);

  const textFieldRef = useRef();
  const uploadFileRef = useRef();
  const [previewFiles, setPreview] = useState([]);
  const [isAddMore, setAddMore] = useState(false);
  const [templateModalVisible, setTemplateModalVisible] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateVariables, setTemplateVariables] = useState({});
  const [formikSetFieldValue, setFormikSetFieldValue] = useState(null);

  // Track input value for product tag detection
  const [inputValue, setInputValue] = useState('');
  const debouncedInput = useDebounce(inputValue, 300);

  useEffect(() => {
    textFieldRef.current.focus();
  }, [conversationId]);

  // Detect @productId pattern and fetch product preview
  useEffect(() => {
    const { hasProductTag, productId } = Helper.parseProductTag(debouncedInput);
    if (hasProductTag && productId !== productPreview.productId) {
      dispatch(fetchProductPreviewRequest({ productId }));
    } else if (!hasProductTag && productPreview.productId) {
      dispatch(clearProductPreview());
    }
  }, [debouncedInput, dispatch, productPreview.productId]);

  // Cleanup on unmount or conversation change
  useEffect(() => {
    return () => {
      dispatch(clearProductPreview());
    };
  }, [conversationId, dispatch]);

  // eslint-disable-next-line space-before-function-paren
  function getBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  }

  const handleRemove = (index) => {
    setPreview((previews) => previews.filter((pre, idx) => idx !== index));
  };

  const extractPlaceholders = (content) => {
    const regex = /\{([^}]+)\}/g;
    const placeholders = [];
    let match;
    while ((match = regex.exec(content)) !== null) {
      if (!placeholders.includes(match[1])) {
        placeholders.push(match[1]);
      }
    }
    return placeholders;
  };

  const replacePlaceholders = (content, variables) => {
    let result = content;
    Object.entries(variables).forEach(([key, value]) => {
      const regex = new RegExp(`\\{${key}\\}`, 'g');
      result = result.replace(regex, value || `{${key}}`);
    });
    return result;
  };

  const handleTemplateSelect = (template) => {
    const placeholders = extractPlaceholders(template.content);
    if (placeholders.length > 0) {
      setSelectedTemplate(template);
      const initialVariables = {};
      placeholders.forEach((placeholder) => {
        initialVariables[placeholder] = '';
      });
      setTemplateVariables(initialVariables);
      setTemplateModalVisible(true);
    } else {
      // No placeholders, use template directly
      if (formikSetFieldValue) {
        formikSetFieldValue('message', template.content);

        // If template has an image, add it to attachments
        if (template.image) {
          const imageUrl = typeof template.image === 'string' ? template.image : template.image.url;
          const imageAttachment = {
            type: 'image',
            localUrl: imageUrl,
            file: null, // Template images don't have file objects
            isTemplateImage: true,
            name: 'template-image', // Add name for preview handling
          };
          formikSetFieldValue('attachments', [imageAttachment]);
          setPreview([imageAttachment]);
        } else {
          // Clear any existing images when selecting template without image
          formikSetFieldValue('attachments', []);
          setPreview([]);
        }
      }
    }
  };

  const handleUseTemplate = (template) => {
    // Use template immediately without modal
    if (formikSetFieldValue) {
      formikSetFieldValue('message', template.content);

      // If template has an image, add it to attachments
      if (template.image) {
        const imageUrl = typeof template.image === 'string' ? template.image : template.image.url;
        const imageAttachment = {
          type: 'image',
          localUrl: imageUrl,
          file: null, // Template images don't have file objects
          isTemplateImage: true,
          name: 'template-image', // Add name for preview handling
        };
        formikSetFieldValue('attachments', [imageAttachment]);
        setPreview([imageAttachment]);
      } else {
        // Clear any existing images when selecting template without image
        formikSetFieldValue('attachments', []);
        setPreview([]);
      }
    } else {
      console.warn('formikSetFieldValue not available');
    }
  };

  const handleTemplateSubmit = () => {
    if (selectedTemplate && formikSetFieldValue) {
      const finalContent = replacePlaceholders(selectedTemplate.content, templateVariables);
      formikSetFieldValue('message', finalContent);

      // If template has an image, add it to attachments
      if (selectedTemplate.image) {
        const imageUrl =
          typeof selectedTemplate.image === 'string' ? selectedTemplate.image : selectedTemplate.image.url;
        const imageAttachment = {
          type: 'image',
          localUrl: imageUrl,
          file: null, // Template images don't have file objects
          isTemplateImage: true,
          name: 'template-image', // Add name for preview handling
        };
        formikSetFieldValue('attachments', [imageAttachment]);
        setPreview([imageAttachment]);
      } else {
        // Clear any existing images when selecting template without image
        formikSetFieldValue('attachments', []);
        setPreview([]);
      }
    }
    setTemplateModalVisible(false);
    setSelectedTemplate(null);
    setTemplateVariables({});
  };

  return (
    <div className="message-input-container">
      <Formik
        initialValues={{ message: '', attachments: [] }}
        onSubmit={(values) => {
          const trimmedMessage = _invoke(values.message, 'trim');

          // Separate template images from regular attachments
          const regularAttachments = values.attachments.filter((att) => !att.isTemplateImage);
          const templateImages = values.attachments.filter((att) => att.isTemplateImage);

          // Convert template images to the format expected by the message handler
          const processedTemplateImages = templateImages.map((img) => ({
            type: 'template-image',
            url: img.localUrl, // Already uploaded URL
            isTemplateImage: true,
          }));

          setPreview([]);
          setInputValue(''); // Reset input value
          dispatch(clearProductPreview()); // Clear product preview on submit
          onClick({
            ...values,
            message: trimmedMessage,
            attachments: regularAttachments, // Regular files that need uploading
            templateImages: processedTemplateImages, // Pre-uploaded template images
          });
        }}
        innerRef={ref}
      >
        {({ handleSubmit, values, setFieldValue }) => {
          const attachments = _get(values, 'attachments', []);

          // Store setFieldValue for template handlers
          if (formikSetFieldValue !== setFieldValue) {
            setFormikSetFieldValue(() => setFieldValue);
          }

          // Track input value for product tag detection (sync with Formik)
          if (values.message !== inputValue) {
            setInputValue(values.message || '');
          }

          return (
            <>
              <ProductPreviewTooltip
                productPreview={productPreview}
                onClose={() => dispatch(clearProductPreview())}
              />
            <Form
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
            >
              <div className="message-form-container">
                <TextInput
                  name="message"
                  inputType="textarea"
                  required
                  ref={textFieldRef}
                  autoSize={{ minRows: 1, maxRows: 3 }}
                />
                <input
                  accept="image/* video/*"
                  multiple
                  type="file"
                  ref={uploadFileRef}
                  style={{ display: 'none' }}
                  onChange={async (e) => {
                    if (!isAddMore) {
                      const promises = Array.from(e.target.files).map(async (file) => {
                        if (file.type.startsWith('video')) {
                          return {
                            type: 'video',
                            localUrl: URL.createObjectURL(file),
                            file,
                          };
                        }
                        return {
                          type: 'image',
                          localUrl: await getBase64(file),
                          file,
                        };
                      });
                      const previews = await Promise.all(promises);
                      setFieldValue('attachments', previews);
                      setPreview(previews);
                    } else {
                      setAddMore(false);
                      if (!_isEmpty(e.target.files)) {
                        setFieldValue('attachments', Array.from(attachments).concat(e.target.files));
                        const promises = Array.from(e.target.files).map(async (file) => {
                          if (file.type.startsWith('video')) {
                            return {
                              type: 'video',
                              localUrl: URL.createObjectURL(file),
                              file,
                            };
                          }
                          return {
                            type: 'image',
                            localUrl: await getBase64(file),
                            file,
                          };
                        });
                        const previews = await Promise.all(promises);
                        setFieldValue('attachments', Array.from(attachments).concat(previews));
                        setPreview((prev) => prev.concat(previews));
                      }
                    }
                  }}
                />
                <div className="input-actions">
                  <div className="template-selector-inline">
                    <TemplateSelector onSelectTemplate={handleTemplateSelect} onUseTemplate={handleUseTemplate} />
                  </div>
                  {_isEmpty(previewFiles) && (
                    <Button
                      loading={uploading}
                      onClick={() => uploadFileRef.current.click('aa')}
                      className="upload-file-btn"
                      icon={<CameraOutlined />}
                    ></Button>
                  )}
                  <Button
                    type="button"
                    className="send-message-btn"
                    icon={<img src={SendIcon} alt="" />}
                    onClick={handleSubmit}
                  ></Button>
                </div>
              </div>
              {uploading && uploadProgress > 0 && (
                <div className="upload-progress-container" style={{ padding: '8px 0' }}>
                  <Progress
                    percent={uploadProgress}
                    size="small"
                    status="active"
                    strokeColor={{
                      '0%': '#108ee9',
                      '100%': '#87d068',
                    }}
                    format={(percent) => `Đang tải lên ${percent}%`}
                  />
                </div>
              )}
              {!_isEmpty(previewFiles) && (
                <div className="preview-container">
                  {previewFiles.map((file, index) => (
                    <div key={file.name || `file-${index}`} className="preview-file-container">
                      <Button
                        onClick={() => {
                          handleRemove(index);
                          setFieldValue(
                            'attachments',
                            Array.from(attachments).filter((att, idx) => idx !== index)
                          );
                        }}
                        type="text"
                        icon={<CloseCircleFilled />}
                        className="close-icon"
                      />
                      {file.type === 'image' || file.isTemplateImage ? (
                        <img
                          alt=""
                          src={file.localUrl}
                          style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4 }}
                        />
                      ) : (
                        <ReactPlayer width={48} height={48} url={file.localUrl} />
                      )}
                    </div>
                  ))}
                  <div className="preview-file-container">
                    <Button
                      onClick={() => {
                        setAddMore(true);
                        uploadFileRef.current.click();
                      }}
                      icon={<FileAddFilled />}
                      style={{ height: '100%' }}
                    />
                  </div>
                </div>
              )}
            </Form>
            </>
          );
        }}
      </Formik>

      <Modal
        title="Điền thông tin cho Template"
        visible={templateModalVisible}
        onOk={handleTemplateSubmit}
        onCancel={() => {
          setTemplateModalVisible(false);
          setSelectedTemplate(null);
          setTemplateVariables({});
        }}
        okText="Sử dụng"
        cancelText="Hủy"
      >
        {selectedTemplate && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <strong>Template: </strong>
              {selectedTemplate.title}
            </div>
            {selectedTemplate.image && (
              <div style={{ marginBottom: 16 }}>
                <strong>Hình ảnh:</strong>
                <div style={{ marginTop: 8 }}>
                  <img
                    src={
                      typeof selectedTemplate.image === 'string' ? selectedTemplate.image : selectedTemplate.image.url
                    }
                    alt="Template"
                    style={{
                      maxWidth: '100%',
                      maxHeight: 200,
                      borderRadius: 4,
                      border: '1px solid #d9d9d9',
                      objectFit: 'cover',
                    }}
                  />
                </div>
              </div>
            )}
            <div style={{ marginBottom: 16 }}>
              <strong>Nội dung gốc:</strong>
              <div
                style={{
                  background: '#f5f5f5',
                  padding: 8,
                  borderRadius: 4,
                  marginTop: 8,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {selectedTemplate.content}
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <strong>Điền thông tin:</strong>
              <Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
                {Object.keys(templateVariables).map((placeholder) => (
                  <div key={placeholder}>
                    <label style={{ marginBottom: 4, display: 'block' }}>{placeholder}:</label>
                    <Input
                      placeholder={`Nhập ${placeholder}`}
                      value={templateVariables[placeholder]}
                      onChange={(e) =>
                        setTemplateVariables((prev) => ({
                          ...prev,
                          [placeholder]: e.target.value,
                        }))
                      }
                    />
                  </div>
                ))}
              </Space>
            </div>
            <div>
              <strong>Kết quả:</strong>
              <div
                style={{
                  background: '#e6f7ff',
                  padding: 8,
                  borderRadius: 4,
                  marginTop: 8,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {replacePlaceholders(selectedTemplate.content, templateVariables)}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
});

MessageInput.propTypes = {
  onClick: PropTypes.func.isRequired,
  conversationId: PropTypes.string.isRequired,
  uploading: PropTypes.bool,
  uploadProgress: PropTypes.number,
  isSending: PropTypes.bool,
};

export default MessageInput;
